/**
 * ContactReauth.jsx — TEST 3.01: "confirm it's you" before an e-mail or mobile number is changed.
 *
 * The server's re-auth for a contact change (_patient_contact_reauth, API lane/a301) is Delete Account's
 * deletion_identity_check: the current password, or a FRESH Apple / Google sign-in whose `sub` matches the account.
 * The method is chosen exactly as Delete Account chooses it (utils/ssoReauth reauthMethodFor: the way the patient
 * signed in; the server's `providers` list wins after a refusal). Nothing here touches the session token.
 */
import { useEffect, useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { useI18n } from '../i18n'
import { authAPI } from '../services/api'
import { useUserStore } from '../stores'
import {
  getFreshProviderToken, reauthMethodFor, serverAskedMethods, providerName, preloadProviderSdk, ssoProvidersFor,
} from '../utils/ssoReauth'
import { reauthFields } from '../utils/contactChange'

const NAVY = '#15354F'

export function useContactReauth() {
  const user = useUserStore((s) => s.user)
  const [accountAuth, setAccountAuth] = useState(null) // auth_provider as the server reports it; null = unknown
  const [chosen, setChosen] = useState(null)
  const [server, setServer] = useState(null)
  const [password, setPassword] = useState('')
  // R301P L3: bumped after a refused re-auth; the password field takes the focus once the screen is not busy.
  const [focusAsk, setFocusAsk] = useState(0)
  const focusDone = useRef(0) // the last ask the field answered (kept here, so a remounted field does not re-answer it)

  useEffect(() => {
    let live = true
    authAPI.get2faStatus()
      .then((st) => { if (live) setAccountAuth(String(st?.authProvider || '')) })
      .catch(() => { /* unknown: the session decides, then the server's answer */ })
    return () => { live = false }
  }, [])

  const plan = reauthMethodFor(accountAuth, { session: user?.signInProvider || null, chosen, server })
  const provider = plan.method === 'apple' || plan.method === 'google' ? plan.method : null
  useEffect(() => { if (provider) preloadProviderSdk(provider) }, [provider])

  // Ready to send: a typed password, or a provider to sign in with right now. With the method unknown (the account
  //   read failed and the session does not say) the send goes without a proof: the server answers 401
  //   reauth_required with its `providers` list, spends nothing, and `refused` switches to that method.
  const ready = plan.method === 'password' ? password.length > 0 : (!!provider || (plan.method === null && !plan.choices.length))

  /**
   * The proof fields for one send. A provider sign-in happens HERE, inside the patient's tap.
   * R301P L1: the password leaves React state as soon as its proof is built, so it is not kept through the code
   * step or the done screen. A resend therefore asks for it again (an Apple / Google proof is a fresh sign-in anyway).
   */
  const prove = async () => {
    if (plan.method === 'password') {
      const fields = reauthFields('password', { password })
      setPassword('')
      return fields
    }
    if (provider) return reauthFields(provider, { token: await getFreshProviderToken(provider) })
    return null
  }

  /** After a refusal of kind 'reauth': follow the server's list, and fall back to the password (as Delete Account). */
  const refused = (r) => {
    if (r.code === 'wrong_password') {
      if (plan.method !== 'password') setServer(['password'])
      setPassword('')
      setFocusAsk((n) => n + 1)
      return
    }
    const asked = serverAskedMethods(r.providers)
    if (asked.length) setServer(asked)
    setFocusAsk((n) => n + 1)
  }

  return {
    method: plan.method, choices: plan.choices, provider, password, setPassword, setChosen, ready, prove, refused, focusAsk, focusDone,
    linkedSso: ssoProvidersFor(accountAuth),
  }
}

/** The field (password) or the line (Apple / Google) that the re-auth needs. */
export default function ContactReauth({ reauth, disabled = false }) {
  const { t } = useI18n()
  const [show, setShow] = useState(false)
  const field = useRef(null)
  // R301P L3: a refused re-auth moves the focus to the password field (after the send, when it is enabled again).
  useEffect(() => {
    if (reauth.focusAsk === reauth.focusDone.current || disabled || !field.current) return
    reauth.focusDone.current = reauth.focusAsk
    field.current.focus()
  }, [reauth.focusAsk, reauth.focusDone, disabled])
  if (reauth.method === 'password') {
    return (
      <div style={{ marginTop: 14 }} data-contact-reauth="password">
        <p id="contact-reauth-hint" style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>{t('contact.confirmIdentity')}</p>
        <div className="relative">
          <input
            id="contact-reauth-password"
            ref={field}
            type={show ? 'text' : 'password'}
            value={reauth.password}
            onChange={(e) => reauth.setPassword(e.target.value)}
            autoComplete="current-password"
            aria-label={t('auth.password')}
            aria-describedby="contact-reauth-hint"
            disabled={disabled}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none pr-12"
          />
          <button type="button" onClick={() => setShow(!show)} aria-label={show ? t('contact.hidePassword') : t('contact.showPassword')}
            aria-controls="contact-reauth-password"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" style={{ background: 'none', border: 'none' }}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
    )
  }
  return (
    <div style={{ marginTop: 14 }} data-contact-reauth="sso">
      {reauth.choices.length > 1 && (
        <div className="flex gap-2" style={{ marginBottom: 8 }}>
          {reauth.choices.map((p) => (
            <button key={p} type="button" data-reauth-provider={p} onClick={() => reauth.setChosen(p)} disabled={disabled}
              style={{ flex: 1, padding: '8px 10px', borderRadius: 10, fontSize: 13, fontWeight: 600, color: NAVY,
                border: reauth.provider === p ? '2px solid #46A8F9' : '1px solid #CBD5E1', background: 'white' }}>
              {providerName(p)}
            </button>
          ))}
        </div>
      )}
      {reauth.provider && (
        <p style={{ fontSize: 13, color: '#475569' }}>{t('contact.confirmIdentitySso', { provider: providerName(reauth.provider) })}</p>
      )}
    </div>
  )
}
